'use client';

import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, ComponentPropsWithRef, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { useChangeLang } from '@/shared/lib/hooks/useChangeLang';
import { Language } from '@/shared/model/Language';
import { WithDataAttributes } from '@/shared/model/WithDataAttributes';
import { Dropdown } from '@/shared/ui/dropdown';
import { Flag } from '@/shared/ui/flag';
import { Layout } from '@/shared/ui/layout';
import './language-select.scss';

type Props = ComponentPropsWithRef<typeof Layout> & {
    isLabelVisible?: boolean;
    iconWrapperProps?: WithDataAttributes<ComponentPropsWithRef<'div'>>;
    valueClassName?: string;
    valueAs?: 'label' | 'shortCut';
    contentProps?: WithDataAttributes<ComponentPropsWithoutRef<typeof Dropdown.Content>>;
    languageItemTestId?: string;
};
export const LanguageSelect = ({
    className,
    isLabelVisible = true,
    iconWrapperProps,
    valueClassName,
    valueAs = 'label',
    contentProps,
    languageItemTestId,
    ...props
}: Props) => {
    const t = useTranslations();
    const { languageOptions, selectedLang, handleChangeLang } = useChangeLang();
    const [isOpen, setIsOpen] = useState(false);
    if (languageOptions.length <= 1) {
        return null;
    }

    return (
        <Dropdown open={isOpen} onOpenChange={setIsOpen}>
            <Layout
                element={Dropdown.Trigger}
                className={cn('language-select__trigger', className)}
                data-value={selectedLang.shortCut}
                data-state={isOpen ? 'open' : 'closed'}
                {...props}
            >
                <div className="language-select__content">
                    <Flag
                        language={selectedLang.flag}
                        data-value={selectedLang.shortCut}
                        {...iconWrapperProps}
                    />

                    <div className="language-select__info">
                        <div className="language-select__text">
                            {isLabelVisible && (
                                <span className="language-select__label">{t('LANGUAGE_SELECT.LABEL')}</span>
                            )}
                            <span className={cn('language-select__value', valueClassName)}>
                                {valueAs === 'label' ? selectedLang.label : selectedLang.shortCut}
                            </span>
                        </div>
                    </div>
                </div>
            </Layout>
            <Dropdown.Content
                className="language-select__dropdown-content"
                onCloseAutoFocus={(e) => e.preventDefault()}
                {...contentProps}
            >
                {languageOptions.map((lang) => {
                    return (
                        <Dropdown.Item
                            key={lang.shortCut}
                            onSelect={() => handleChangeLang(lang.shortCut as Language)}
                            data-testid={languageItemTestId}
                            data-value={lang.shortCut}
                        >
                            {lang.label}
                        </Dropdown.Item>
                    );
                })}
            </Dropdown.Content>
        </Dropdown>
    );
};
